import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { fetchOutstandingPurchaseOrders, createSelectedOutstandingPurchaseOrders } from '../../../services/purchaseOrder';

const OutstandingPOsDialog = ({ open, onClose, salesOrderId, onGenerated }) => {
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');

  useEffect(() => {
    if (!open || !salesOrderId) return;
    let alive = true;
    setLoading(true);
    setError('');
    setResult('');
    setPreview(null);
    fetchOutstandingPurchaseOrders(salesOrderId)
      .then((data) => {
        if (!alive) return;
        setPreview(data);
        setSelected(Object.fromEntries((data.groups || [])
          .filter((group) => group.shouldGenerate && !group.alreadyGenerated)
          .map((group) => [group.groupKey, true])));
      })
      .catch((err) => { if (alive) setError(err.response?.data?.message || 'Unable to review outstanding POs.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, salesOrderId]);

  const groups = preview?.groups || [];
  const chosen = useMemo(() => groups
    .filter((group) => group.shouldGenerate && !group.alreadyGenerated && selected[group.groupKey])
    .map((group) => group.groupKey), [groups, selected]);

  const generate = async () => {
    if (!chosen.length || submitting) return;
    setSubmitting(true);
    setError('');
    setResult('');
    try {
      const response = await createSelectedOutstandingPurchaseOrders(salesOrderId, chosen);
      setResult(response?.message || 'Selected purchase orders generated.');
      const refreshed = await fetchOutstandingPurchaseOrders(salesOrderId);
      setPreview(refreshed);
      // Keep deliberate skips; any newly discovered eligible group defaults
      // to Create new PO, just as it does on first opening this review.
      setSelected(Object.fromEntries((refreshed.groups || [])
        .filter((group) => group.shouldGenerate && !group.alreadyGenerated)
        .map((group) => [group.groupKey, selected[group.groupKey] !== false])));
      await onGenerated?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Generation failed. Review the outstanding candidates and retry.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Generate Outstanding Purchase Orders</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Existing purchase orders are preserved. Only genuinely outstanding purchasing groups can be generated.
          Choose Don't generate for any new group you want to leave for later.
        </Typography>
        {loading && <CircularProgress size={24} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {result && <Alert severity="success" sx={{ mb: 2 }}>{result}</Alert>}
        {!loading && preview && <Table size="small">
          <TableHead><TableRow>
            <TableCell>PO / purchasing group</TableCell>
            <TableCell>Lines</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Action</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {(preview.existingPurchaseOrders || []).map((po) => <TableRow key={`existing-${po.id}`} sx={{ opacity: 0.5 }}>
              <TableCell><Typography variant="body2" fontWeight={600}>{po.poNumber || `PO #${po.id}`}</Typography><Typography variant="caption">{po.supplierName}</Typography></TableCell>
              <TableCell>{(po.orderItemIds || []).join(', ') || 'Service / artwork'}</TableCell>
              <TableCell>Already generated</TableCell>
              <TableCell><Typography variant="body2" color="text.secondary">Preserved · no changes</Typography></TableCell>
            </TableRow>)}
            {groups.filter((group) => !group.alreadyGenerated).map((group) => {
              const existing = Boolean(group.alreadyGenerated);
              const ready = group.shouldGenerate === true && !existing;
              const matchingGeneratedPos = ready && Array.isArray(group.possibleMergePurchaseOrders)
                ? group.possibleMergePurchaseOrders : [];
              const matchedLabels = matchingGeneratedPos.slice(0, 3)
                .map((po) => po.poNumber || `PO #${po.id}`).join(', ');
              const mergeLabel = matchingGeneratedPos.length === 1
                ? `Merge with ${matchedLabels} (coming soon)`
                : `Merge with existing PO (${matchedLabels}${matchingGeneratedPos.length > 3 ? ', …' : ''}) — coming soon`;
              return <TableRow key={group.groupKey} sx={{ opacity: existing ? 0.52 : 1 }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{existing ? (group.existingPoNumber || 'Existing PO') : (group.supplierName || group.routeSummary || 'New Purchase Order')}</Typography>
                  <Typography variant="caption" color="text.secondary">{group.routeSummary || group.type || ''}</Typography>
                </TableCell>
                <TableCell>{(group.orderItemIds || []).join(', ') || 'Service / artwork'}</TableCell>
                <TableCell>{existing ? 'Already generated' : ready ? 'Ready to generate' : group.archivedPurchaseOrderIds?.length ? 'Archived PO exists' : 'Needs attention'}</TableCell>
                <TableCell>
                  {existing ? <Typography variant="body2" color="text.secondary">Preserved · no changes</Typography> :
                    ready ? <RadioGroup value={selected[group.groupKey] ? 'generate' : 'skip'} onChange={(_event, value) => {
                      if (value !== 'generate' && value !== 'skip') return;
                      setSelected(prev => ({ ...prev, [group.groupKey]: value === 'generate' }));
                    }}>
                      <FormControlLabel value="generate" control={<Radio size="small" />} label="Create new PO" disabled={submitting} />
                      {matchingGeneratedPos.length > 0 && (
                        <FormControlLabel value="merge" control={<Radio size="small" />}
                          label={mergeLabel} disabled
                        />
                      )}
                      <FormControlLabel value="skip" control={<Radio size="small" />} label="Don't generate" disabled={submitting} />
                    </RadioGroup> :
                    <Typography variant="body2" color="text.secondary">{group.archivedPurchaseOrderIds?.length ? 'Restore the archived PO before generating a replacement.' : ((group.shippingIssues || []).map(x => x.reason).concat((group.routingIssues || []).map(x => x.message || x.reason)).concat((group.proofIssues || []).map(x => x.reason)).filter(Boolean).join('; ') || 'Check shipping, routing or proofs')}</Typography>}
                </TableCell>
              </TableRow>;
            })}
          </TableBody>
        </Table>}
        {!loading && preview && groups.length === 0 && <Typography>No outstanding PO candidates were found.</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Close</Button>
        <Button variant="contained" onClick={generate} disabled={loading || submitting || !chosen.length}>
          {submitting ? 'Generating…' : `Generate ${chosen.length} selected PO${chosen.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OutstandingPOsDialog;
